import React from 'react';
import classNames from 'classnames';

import {
    useResidenceExplorerStore,
    useConnectedObjects,
    useViewApartments,
    selectAllViews,
    selectCurrentView,
    selectExplorer,
    selectHideObjects,
    selectResidenceExplorerSettings,
    selectSelectedObjectId,
    selectSetup,
} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {isDefined} from "features/services/utils/helpers";
import {ObjectStatus} from "features/search/filters/enums";
import {ClickAreaType} from "features/residenceExplorer/enums";
import {getFormattedMessage} from "features/services/utils/language-helper";
import {ResidenceExplorerClickArea as ResidenceExplorerClickAreaType} from "features/residenceExplorer/types";

import './_residenceExplorerClickArea.scss';
import {useNavigate} from 'react-router';


export const ResidenceExplorerClickArea: React.FC = ({}) => {
    const explorer = useResidenceExplorerStore(selectExplorer);
    const settings = useResidenceExplorerStore(selectResidenceExplorerSettings);
    const setup = useResidenceExplorerStore(selectSetup);
    const currentView = useResidenceExplorerStore(selectCurrentView);
    const views = useResidenceExplorerStore(selectAllViews);
    const connectedObjects = useConnectedObjects();
    const selectedObjectId = useResidenceExplorerStore(selectSelectedObjectId);
    const hideObjects = useResidenceExplorerStore(selectHideObjects);
    const setCurrentViewName = useResidenceExplorerStore(state => state.setCurrentViewName);
    const setSelectedObjectId = useResidenceExplorerStore(state => state.setSelectedObjectId);
    const navigate = useNavigate();

    const viewApartments = useViewApartments();

    if (!setup || !settings || !currentView) return null;



    const { viewBoxHeight = 0, viewBoxWidth = 0 } = settings;

    const onChangeViewInternal = (clickArea: ResidenceExplorerClickAreaType) => (e: React.SyntheticEvent) => {
        switch (clickArea.type as ClickAreaType) {
            case ClickAreaType.APT:
                onApartmentClick(clickArea.value.toUpperCase(), e);
                break;
            case ClickAreaType.VIEW:
                onViewClick(clickArea.value);
                break;
            case ClickAreaType.EXT:
                onExtClick(clickArea.value);
                break;
        }
    };

    const checkObjectStatus = (objectId: string): ObjectStatus | null => {
        if (!connectedObjects) {
            return null;
        }

        const normalizedId = objectId.toUpperCase();
        const object = connectedObjects[normalizedId];

        return object ? object.objectStatus : ObjectStatus.NOT_AVAILABLE;
    };


    const onApartmentClick = (value: string, _e: unknown) => {
        setSelectedObjectId(value);
    };

    const onViewClick = (value: string) => {
        setCurrentViewName(value);
    };

    const onExtClick = (value: string) => {
        const url = new URL(value);
        navigate(url.pathname);
    };

    const checkIfClickable = (clickArea: ResidenceExplorerClickAreaType) => {
        if (clickArea.type == 'ext' && clickArea.value != "") return true;

        const viewName = clickArea.value;
        if(!views || typeof views[viewName] === 'undefined') return false;
        return true;
    };

    const getViewDescription = (clickArea: ResidenceExplorerClickAreaType) => {
        if (clickArea.extDescription && clickArea.extDescription != "") return <title>{clickArea.extDescription}</title>;

        const viewName = clickArea.value;
        if(!views || typeof views[viewName] === 'undefined') return null;
        return views[viewName] != null && views[viewName].description != "" ?
            <title>{views[viewName].description}</title> :
            null;
    };

    const renderPath = (clickArea: ResidenceExplorerClickAreaType, pathClass: string, clickable: boolean) => (
        <path
            d={clickArea.coords.startsWith("M") ? clickArea.coords : `M ${clickArea.coords} Z`}
            key={`path-${clickArea.coords}`}
            onClick={clickable ? onChangeViewInternal(clickArea) : undefined}
            className={pathClass}
        >
            {getViewDescription(clickArea)}
        </path>
    );

    const getActiveObject = (aptId: string) => {
        if(connectedObjects === null) return false;
        if (typeof setup.currentObjectId === 'undefined' || selectedObjectId) return false;
        if (!isDefined(connectedObjects[aptId.toUpperCase()])) return false;

        return connectedObjects[aptId.toUpperCase()].apartmentNumber.toUpperCase() === setup.currentObjectId.toUpperCase();
    };

    const viewHasObjects = (name: string) => {

        if(explorer?.explorerType !== "Visuado") return true;

        if(viewApartments === null || connectedObjects === null) return false;

        // Checking if current view only is apartments. Then skip this logic
        const topNodes = Object.keys(currentView.clickArea);
        let apartmentsOnly = true;

        topNodes.forEach(node => {
            if(currentView.clickArea[node].type !== "apt") {
                apartmentsOnly = false;
            }
        })

        if(apartmentsOnly) return true;

        const viewApartmentList: string[] = viewApartments[name];
        const apartmentList: string[] = Object.keys(connectedObjects);

        if(!viewApartmentList || !apartmentList) return false;

        return viewApartmentList.some(apartment => apartmentList.includes(apartment));
    }

    const getPathClass = (clickArea: ResidenceExplorerClickAreaType, status: ObjectStatus, active: boolean, clickable: boolean, _notShown?: boolean): string => {
        return classNames('residence-explorer-view__path', {

            //'residence-explorer-view__path--not-shown': notShown,
            'residence-explorer-view__path--for-sale': status === ObjectStatus.FOR_SALE,
            'residence-explorer-view__path--reserved': status === ObjectStatus.RESERVED,
            'residence-explorer-view__path--sold': status === ObjectStatus.SOLD || clickArea.type === ClickAreaType.EXT && clickArea.extColor === "red",
            'residence-explorer-view__path--booked': status === ObjectStatus.BOOKED,
            'residence-explorer-view__path--showing-apartment': status === ObjectStatus.SHOWING_APARTMENT,
            'residence-explorer-view__path--active': selectedObjectId === clickArea.value.toUpperCase() || active,
            'residence-explorer-view__path--inactive': !clickable,
        });
    };

    if(!currentView.clickArea) return null;

    return (
        <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} className="residence-explorer-click-area">
            {Object.entries(currentView.clickArea).map(([key, clickArea]) => {
                if (!clickArea.coords) return null;

                // TODO: Readd this
                if(!viewHasObjects(key)) return null;
                if(setup.hideSoldObjectsInExplorer && clickArea.type === ClickAreaType.APT && checkObjectStatus(clickArea.value) === ObjectStatus.SOLD) return null;

                const click = {...clickArea};

                let status : ObjectStatus | null = null;
                let active = false;
                let clickable = true;

                if(hideObjects) return null;
                if (click.type === ClickAreaType.APT) {
                    status = checkObjectStatus(click.value);

                    active = getActiveObject(click.value);
                    if (status === ObjectStatus.NOT_AVAILABLE || (status === ObjectStatus.SOLD && setup.hideSoldObjects)) return null;

                    if (status === ObjectStatus.SHOWING_APARTMENT) {
                        click.extDescription = getFormattedMessage('jm.components.ResidenceTags.showingApartment');
                    }
                } else {
                    clickable = checkIfClickable(click);
                }

                const pathClass = getPathClass(click, status!, active!, clickable, !viewHasObjects(key));

                return click.coords.includes(";") ? (
                    <g key={`group-${key}`}>
                        {click.coords.split(";").map((_coords) =>
                            renderPath(click, pathClass, clickable)
                        )}
                    </g>
                ) : (
                    renderPath(click, pathClass, clickable)
                );
            })}
        </svg>
    );
};
